import {
  AddressFieldMiddleware,
  type AddressFieldValue,
  useAddressFieldCompletion,
} from '@adornis/address/client/AddressFieldMiddleware.js';
import { isNotNullish } from '@adornis/assert/isNullish.js';
import type { EntityData } from '@adornis/baseql/entities/types.js';
import { DropdownSelectionInput } from '@adornis/formfield/fields/dropdown-selection-input.js';
import { TextInput } from '@adornis/formfield/fields/text-input.js';
import type { FunctionalFormFieldProps } from '@adornis/formfield/functional-form-field.js';
import type { FunctionalForm } from '@adornis/forms/use-form.js';
import { stateful } from '@adornis/functional-lit/stateful.js';
import { FormGridEntry } from '@martinus/styles/client/FormGridEntry.js';
import { html, nothing } from 'lit';

interface AddressFormProps {
  form: FunctionalForm<EntityData<Record<string, unknown>>>;
  streetAndNumberFieldPath: [string, string?, string?];
  zipFieldPath?: [string, string?, string?];
  districtFieldPath?: [string, string?, string?];
  cityFieldPath?: [string, string?, string?];
  countryFieldPath?: [string, string?, string?];
  auslandsPlzFieldPath?: [string, string?, string?];
  attentionLineFieldPath?: [string, string?, string?];
}

export const AddressForm = stateful((props: AddressFormProps) => {
  const addressCompletion = useAddressFieldCompletion({ language: 'de' });

  // Paths are runtime-only, so we cast through unknown to satisfy the form.field signature
  const asFormfield = (path: [string, string?, string?]) =>
    (props.form.field as unknown as (...p: string[]) => Partial<FunctionalFormFieldProps<AddressFieldValue>>)(
      ...path.filter(isNotNullish),
    );
  const asDropdownProps = <T>(obj: T) => obj as unknown as Parameters<typeof DropdownSelectionInput>[0];
  const asTextInputProps = <T>(obj: T) => obj as unknown as Parameters<typeof TextInput>[0];

  return html`
    <div class="form-grid">
      ${
        props.streetAndNumberFieldPath
          ? FormGridEntry({
              label: 'Straße und Hausnummer',
              render: DropdownSelectionInput(
                asDropdownProps(
                  AddressFieldMiddleware({
                    ...asFormfield(props.streetAndNumberFieldPath),
                    fieldType: 'address',
                    addressCompletion,
                    placeholder: 'Straße und Hausnummer eingeben',
                    emptyState: 'Keine Adresse gefunden',
                    placeholderMode: 'hide',
                    clearable: true,
                    freeText: true,
                  }),
                ),
              ),
            })
          : nothing
      }
      ${
        props.attentionLineFieldPath
          ? FormGridEntry({
              label: 'Anschriftenzusatz',
              render: TextInput(
                asTextInputProps({
                  ...asFormfield(props.attentionLineFieldPath),
                  placeholder: 'Kein Anschriftenzusatz hinterlegt',
                  placeholderMode: 'hide',
                  clearable: true,
                }),
              ),
            })
          : nothing
      }
      ${
        props.zipFieldPath && props.cityFieldPath
          ? FormGridEntry({
              label: 'Stadt / PLZ',
              render: html`
                <div class="flex flex-row gap-2">
                  ${DropdownSelectionInput(
                    asDropdownProps(
                      AddressFieldMiddleware({
                        ...asFormfield(props.cityFieldPath),
                        fieldType: 'city',
                        addressCompletion,
                        placeholder: 'Ort eingeben',
                        emptyState: 'Kein Ort gefunden',
                        placeholderMode: 'hide',
                        clearable: true,
                        freeText: true,
                      }),
                    ),
                  )}
                  ${DropdownSelectionInput(
                    asDropdownProps(
                      AddressFieldMiddleware({
                        ...asFormfield(props.zipFieldPath),
                        fieldType: 'zip',
                        addressCompletion,
                        placeholder: 'PLZ eingeben',
                        emptyState: 'Keine PLZ gefunden',
                        placeholderMode: 'hide',
                        clearable: true,
                        freeText: true,
                      }),
                    ),
                  )}
                </div>
              `,
            })
          : nothing
      }
      ${
        props.districtFieldPath
          ? FormGridEntry({
              label: 'Stadtteil',
              render: TextInput(
                asTextInputProps(
                  AddressFieldMiddleware({
                    ...asFormfield(props.districtFieldPath),
                    fieldType: 'district',
                    addressCompletion,
                    placeholder: 'Stadtteil eingeben',
                    emptyState: 'Kein Stadtteil hinterlegt',
                    placeholderMode: 'hide',
                    clearable: true,
                    freeText: true,
                  }),
                ),
              ),
            })
          : nothing
      }
      ${
        props.countryFieldPath
          ? FormGridEntry({
              label: 'Land',
              render: DropdownSelectionInput(
                asDropdownProps(
                  AddressFieldMiddleware({
                    ...asFormfield(props.countryFieldPath),
                    fieldType: 'country',
                    addressCompletion,
                    placeholder: 'Land eingeben',
                    emptyState: 'Kein Land gefunden',
                    placeholderMode: 'hide',
                    clearable: true,
                  }),
                ),
              ),
            })
          : nothing
      }
      ${
        props.auslandsPlzFieldPath
          ? FormGridEntry({
              label: 'ggf. Auslands-PLZ',
              render: TextInput(
                asTextInputProps({
                  ...asFormfield(props.auslandsPlzFieldPath),
                  placeholder: 'Keine Auslands-PLZ hinterlegt',
                  placeholderMode: 'hide',
                  clearable: true,
                }),
              ),
            })
          : nothing
      }
    </div>
  `;
});
